import { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, MenuItem, Divider } from '@mui/material';
import { DropMenu, Identicon } from '@art/shared-ui';
import FaIcon from "../utils/fa_icon";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import SidebarTooltip from './sidebar_tooltip';
import ProfileMenuContent from '../header/profile-menu';
import type { SettingsCategory } from '../header/settings-menu';

interface MenuItem {
  title?: string;
  to?: string;
  key?: string;
  role?: string;
  prefix?: string;
  divider?: boolean;
  dataQa?: string;
}

interface MobileMenuDropdownProps {
  icon: IconDefinition | 'identicon';
  label: string;
  title: string;
  menuItems: MenuItem[];
  isToggled: boolean;
  userEmail?: string;
  onItemClick?: (item: MenuItem) => void;
  onClose?: () => void;
  currentPath?: string;
  filterByRole?: boolean;
  userRole?: any[];
  profileUser?: any;
  tenantName?: string | null;
  settingsCategories?: SettingsCategory[];
}

const MobileMenuDropdown = memo(({
  icon,
  label,
  title,
  menuItems,
  isToggled,
  userEmail,
  onItemClick,
  onClose,
  currentPath,
  filterByRole = false,
  userRole = [],
  profileUser,
  tenantName,
  settingsCategories,
}: MobileMenuDropdownProps) => {
  const location = useLocation();
  const path = currentPath || location.pathname;

  const filteredItems = filterByRole
    ? menuItems.filter((item) => {
        if (!item.role) return true;
        return userRole?.some((role: any) => role.name === item.role);
      })
    : menuItems;

  const handleItemClick = (item: MenuItem, close: () => void) => {
    if (onItemClick) {
      onItemClick(item);
    }
    if (onClose) {
      onClose();
    }
    close();
  };

  return (
    <SidebarTooltip
      title={label}
      placement="right"
      disableHoverListener={!isToggled}
      disableFocusListener={!isToggled}
      disableTouchListener={!isToggled}
    >
      <DropMenu
        menuWidth={profileUser ? "min(324px, calc(100vw - 20px))" : "220px"}
        menuPadding={profileUser ? "0" : "15px"}
        triggerComponent={
          <div className={`art_mobile_menu_item ${isToggled ? 'toggled' : ''}`} title={title}>
            {icon === 'identicon' ? (
              <div className="art_mobile_icon">
                <Identicon
                  size={24}
                  value={userEmail || ''}
                  config={{ backColor: "#ede6e6", padding: 0.12 }}
                  style={{ borderRadius: 4 }}
                />
              </div>
            ) : (
              <FaIcon icon={icon as IconDefinition} className="art_mobile_icon" />
            )}
            {!isToggled && <span className="art_mobile_label">{label}</span>}
          </div>
        }
        children={(close: () => void) => (
          profileUser && settingsCategories ? (
            <ProfileMenuContent
              user={profileUser}
              tenantName={tenantName}
              categories={settingsCategories}
              pathname={path}
              onNavigate={() => {
                if (onClose) onClose();
                close();
              }}
            />
          ) : <Box onClick={() => close()}>
            {filteredItems.map((item, index) => {
              if (item.divider) {
                return <Divider key={`divider-${index}`} />;
              }

              if (!item.to) return null;

              const isActive = item.prefix
                ? path.includes(`/${item.prefix}`)
                : path === item.to;

              const isExternal = !item.to.startsWith('/');

              return (
                <MenuItem
                  key={item.key || item.to || index}
                  selected={isActive}
                  component={!isExternal ? Link : "a"}
                  to={!isExternal ? item.to : undefined}
                  href={isExternal ? item.to : undefined}
                  onClick={() => handleItemClick(item, close)}
                  data-qa={`mobile-drop-menu-${item.dataQa || item.key}`}
                  sx={{
                    bgcolor: isActive ? "primary.light" : "inherit",
                    fontWeight: isActive ? "600" : "normal",
                    color: isActive ? "primary.main" : "secondary",
                  }}
                >
                  {item.title}
                </MenuItem>
              );
            })}
          </Box>
        )}
      />
    </SidebarTooltip>
  );
});

MobileMenuDropdown.displayName = 'MobileMenuDropdown';

export default MobileMenuDropdown;
